import {useMemo} from 'react'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import {SETTINGS_TAB_PERMISSIONS, SIDEBAR_PERMISSIONS} from '@shared/utils/navigationPermissions'

export interface PermittedNavigationInterface {
  /** True when the user holds at least one of `codenames`. */
  hasAnyPermission: (codenames: readonly string[]) => boolean
  /** True when the given Settings sub-tab id should be shown. */
  canViewSettingsTab: (tabId: string) => boolean
  /** True when the given sidebar item id should be shown. */
  canViewSidebarItem: (itemId: string) => boolean
  /**
   * Which tab a Settings section should render: the requested one when the user
   * may see it, else the section's first visible tab, else `null` (the section
   * has nothing to show, e.g. on direct-URL access to an area the user has no
   * permission in). `orderedTabIds` must be in sub-menu order so the fallback
   * lands on the same tab clicking the section in the sub-menu would.
   */
  resolveSettingsTab: (
    requestedTab: string | null,
    orderedTabIds: readonly string[],
  ) => string | null
}

/**
 * Navigation visibility derived from the organizations permissions the user
 * actually holds (`effective_permissions` on the current-user payload), mapped
 * through `navigationPermissions.ts`.
 *
 * Two deliberate fail-open cases:
 *
 * - **No `effective_permissions` in the payload** (older organizations
 *   deployment) means "show everything", so an out-of-step backend can never
 *   strand a user in an empty app. An explicit empty array is authoritative and
 *   does reduce the menu. Mirrors `useServiceCapabilities`.
 * - **A read-only platform admin** (`is_read_only_admin`: staff holding no real
 *   management capability) may view every admin area, so every entry stays
 *   visible; the edit controls inside are gated separately by `isReadOnly`.
 *
 * An entry with no mapping is always visible — a newly added tab shows up until
 * it is mapped rather than silently disappearing.
 */
export const usePermittedNavigation = (): PermittedNavigationInterface => {
  // Tolerate a missing UserProvider the same way useServiceCapabilities does
  // (the public Alpinist demo embed renders a minimal provider stack).
  const {currentUser} = (useUserContext() as UserContextProps | null) ?? {currentUser: null}
  // Read straight off the profile rather than through useIsReadOnly, which
  // assumes a UserProvider is mounted.
  const isReadOnly = currentUser?.userprofile?.is_read_only_admin === true
  const granted = currentUser?.userprofile?.effective_permissions

  return useMemo(() => {
    // `undefined` -> unknown, show everything. An empty array is a real answer.
    const showEverything = isReadOnly || granted === undefined
    const held = new Set(granted ?? [])

    const hasAnyPermission = (codenames: readonly string[]): boolean =>
      showEverything || codenames.some((codename) => held.has(codename))

    const canView = (table: Record<string, readonly string[]>, key: string): boolean => {
      const required = table[key]
      // Unmapped entry: nothing claims it needs a permission, so show it.
      return required === undefined ? true : hasAnyPermission(required)
    }

    const canViewSettingsTab = (tabId: string): boolean => canView(SETTINGS_TAB_PERMISSIONS, tabId)

    return {
      hasAnyPermission,
      canViewSettingsTab,
      canViewSidebarItem: (itemId: string): boolean => canView(SIDEBAR_PERMISSIONS, itemId),
      resolveSettingsTab: (
        requestedTab: string | null,
        orderedTabIds: readonly string[],
      ): string | null => {
        if (requestedTab !== null && canViewSettingsTab(requestedTab)) {
          return requestedTab
        }
        return orderedTabIds.find((tabId) => canViewSettingsTab(tabId)) ?? null
      },
    }
  }, [granted, isReadOnly])
}
